import React from 'react';
import { Body5, Body7, Container } from '@ui';
import { PREVIEW_STATUS } from '@services/scoreSpecs';
import { formatCount, formatWindow } from './format';
import { hasNoScores, isKnownStatus, modelDrift, statusLabel, windowDrift } from './previewResult';
import { MAX_PREVIEW_UNITS } from './previewSelection';
import { Consequence } from './styles';

// The envelope of a preview run: the facts about the run as a whole that no single result card can
// carry — how many company-units ran and how they ended, whether the cap cut the selection short,
// which window the engine actually resolved, and which model spent the money.
//
// This is the ONLY live region in the dock. The result cards below it are deliberately not in one:
// twenty-five cards read aloud is a wall of speech nobody can sit through, and the thing a
// screen-reader user needs the moment a paid run lands is this summary, once.
//
// Two numbers that look interchangeable are not:
//   * The counts here are tallied off the ROWS. `run.count` is never read, never differenced —
//     the contract does not say whether it is the rows returned or the size of the selection
//     before the cap (services/scoreSpecs.js:213-216), so "N of count" would put a coverage figure
//     on screen that nobody has confirmed. `truncated` answers the coverage question instead.
//   * `truncated` never says "the first 25". The contract promises no ordering, so which 25 came
//     back is not a fact this panel has.
//
// A preview run is real spend and the response carries no cost figure at all, so the panel says so
// rather than leaving an operator to assume the silence means free.

const UNRECOGNISED = 'unrecognised';

// The failure clauses, in the order an operator triages them: a prompt that missed is a different
// job from a unit that never had context to miss on.
const BREAKDOWN_PHRASES = {
  [PREVIEW_STATUS.FAILED]: (n) => `${formatCount(n)} failed extraction`,
  [PREVIEW_STATUS.FAILED_RESOLVE]: (n) => `${formatCount(n)} had nothing to run on`,
};

const ORDERED_FAILURES = [PREVIEW_STATUS.FAILED, PREVIEW_STATUS.FAILED_RESOLVE];

// A status the engine sends that this file has no sentence for is still counted, under the name
// previewResult already knows. Bucketing it with the successes would report a run as better than
// it was; dropping it would make the clauses stop adding up to the total.
const clauseFor = (status, n) => {
  const phrase = BREAKDOWN_PHRASES[status];
  if (phrase) return phrase(n);
  const label = statusLabel(status);
  return label ? `${formatCount(n)} ${label.toLowerCase()}` : null;
};

const tallyStatuses = (results) =>
  results.reduce((acc, result) => {
    const status = (result || {}).status;
    const bucket = isKnownStatus(status) ? status : UNRECOGNISED;
    acc[bucket] = (acc[bucket] || 0) + 1;
    return acc;
  }, {});

const breakdownClauses = (counts) => {
  const others = Object.keys(counts).filter(
    (status) =>
      status !== PREVIEW_STATUS.COMPLETED && status !== UNRECOGNISED && ORDERED_FAILURES.indexOf(status) === -1
  );
  const clauses = ORDERED_FAILURES.concat(others)
    .filter((status) => (counts[status] || 0) > 0)
    .map((status) => clauseFor(status, counts[status]))
    .filter(Boolean);
  const unrecognised = counts[UNRECOGNISED] || 0;
  if (unrecognised > 0) {
    clauses.push(`${formatCount(unrecognised)} came back with a status this panel does not interpret`);
  }
  return clauses;
};

// `ran === 1` is the cheapest and most common first run — one ticker, latest per company.
const units = (n) => `company-unit${n === 1 ? '' : 's'}`;

// A row the engine called completed but that carries no score is NOT a scored unit. Counting it as
// one would put "Scored all 3" above a card reading "returned no scores" — the exact disagreement
// the card is there to surface.
const scoredCompleted = (results) =>
  results.filter(
    (result) => (result || {}).status === PREVIEW_STATUS.COMPLETED && !!(result || {}).scores && !hasNoScores(result)
  ).length;

const countSentence = (results) => {
  const ran = results.length;
  const counts = tallyStatuses(results);
  const completed = counts[PREVIEW_STATUS.COMPLETED] || 0;
  const scored = scoredCompleted(results);
  const clauses = breakdownClauses(counts);
  const emptyCompleted = completed - scored;
  if (emptyCompleted > 0) {
    clauses.push(`${formatCount(emptyCompleted)} completed without a score`);
  }
  const tail = clauses.length > 0 ? `; ${clauses.join(', ')}.` : '.';

  if (ran === 0) return 'The run came back with no company-units.';
  // An all-failed run is the case this line exists for. "Scored 0 of 5" leads with the word
  // "Scored" and reads as a result; nothing was scored, and the sentence says that first.
  if (scored === 0) {
    return `Nothing scored: none of the ${formatCount(ran)} ${units(ran)} that ran produced a score${tail}`;
  }
  if (scored === ran) return `Scored all ${formatCount(ran)} ${units(ran)} that ran${tail}`;
  return `Scored ${formatCount(scored)} of ${formatCount(ran)} ${units(ran)} that ran${tail}`;
};

export const PreviewRunHeader = ({ run, askedWindow, bufferModel }) => {
  if (!run) return null;

  const results = Array.isArray(run.results) ? run.results : [];
  // Both ends can be absent. Rendering `— → —` would claim the engine reported a window it did
  // not, so the line is omitted instead.
  const hasResolvedWindow = !!(run.resolvedFrom || run.resolvedTo);
  const driftedWindow = hasResolvedWindow && windowDrift(askedWindow, { from: run.resolvedFrom, to: run.resolvedTo });
  const driftedModel = modelDrift(run.model, bufferModel);

  return (
    <div role="status">
      <Body5>{countSentence(results)}</Body5>

      {run.truncated && (
        <Container top={6}>
          <Consequence $tone="warn">
            {`Capped at ${MAX_PREVIEW_UNITS} company-units. The selection had more, so this is a sample, not the answer — do not read it as coverage. Narrow the universe or the window to choose what you get.`}
          </Consequence>
        </Container>
      )}

      {hasResolvedWindow && (
        <Container top={6}>
          <Body7 isLowContrast>{`Ran over ${formatWindow(run.resolvedFrom, run.resolvedTo)}`}</Body7>
        </Container>
      )}

      {/* Not warn-toned: the engine clamping a window to what it holds is ordinary, and the
          operator only needs to know which of the two windows the rows below describe. */}
      {driftedWindow && (
        <Container top={6}>
          <Consequence>
            {`Asked for ${formatWindow(askedWindow.from, askedWindow.to)}, resolved to ${formatWindow(
              run.resolvedFrom,
              run.resolvedTo
            )}. These results describe the resolved window.`}
          </Consequence>
        </Container>
      )}

      {run.model && (
        <Container top={6}>
          <Body7 isLowContrast>{`Ran on ${run.model}`}</Body7>
        </Container>
      )}

      {driftedModel && (
        <Container top={6}>
          <Consequence $tone="warn">
            {`The buffer on screen names ${bufferModel}; this run used ${run.model}, the model in the saved spec. Save and run again to test what is being edited.`}
          </Consequence>
        </Container>
      )}

      <Container top={6}>
        <Body7 isLowContrast>No cost is reported for a preview run.</Body7>
      </Container>
    </div>
  );
};
